/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Divider,
  NumberFormatter,
  Skeleton,
  Table
} from '@mantine/core';
import AppStack from '../../common/AppStack';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { REG_FORM_SUBMISSION_STATUS_ENUM } from '../../../config/constants';
import VendorOrderProductPreview from './VendorOrderProductPreview';
import RegCartProductPreview from './RegCartProductPreview';
import { getRegFormSubmissionStatusInfo } from '../../../helpers/registrationHelper';
import OrderProductsCostInfo from './OrderProductsCostInfo';

const OrderRegistrationsInfoCard = ({
  title,
  description,
  productData,
  hideStatus,
  loading,
  actionButton,
  isCancelled,
  onViewTransactions,
  emptyMessage,
  urlBase
}) => {
  const productDataStatusInfo = productData.reduce(
    (r, c) => {
      switch (c.fkRegFormSubmissionStatus?.toString()) {
        case REG_FORM_SUBMISSION_STATUS_ENUM.PENDING_PAYMENT:
          r.pendingCount += 1;
          break;
        case REG_FORM_SUBMISSION_STATUS_ENUM.DENIED:
          r.deniedCount += 1;
          break;
        case REG_FORM_SUBMISSION_STATUS_ENUM.REFUNDED:
          r.refundedCount += 1;
          break;
        case REG_FORM_SUBMISSION_STATUS_ENUM.ACCEPTED:
          r.acceptedCount += 1;
          break;
        default:
          break;
      }

      return r;
    },
    {
      deniedCount: 0,
      acceptedCount: 0,
      refundedCount: 0,
      pendingCount: 0
    }
  );
  const hasPendingProducts = productDataStatusInfo.pendingCount > 0;
  const isCompleted = !hasPendingProducts;

  return !loading && productData ? (
    <AppCard radius="md" shadow="xs" style={{ padding: 0 }} withBorder>
      <AppStack style={{ gap: 0 }}>
        <AppFlexbox
          style={{
            padding: 16,
            paddingBottom: 8,
            gap: 8,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <AppFlexbox style={{ alignItems: 'center' }}>
            <AppText style={{ fontSize: 16, fontWeight: 500 }}>
              {title || 'Products'}
            </AppText>

            {!hideStatus &&
              (isCancelled ? (
                <Badge color="#C40000" variant="filled">
                  Cancelled
                </Badge>
              ) : isCompleted ? (
                <Badge color="green" size="md">
                  Completed
                </Badge>
              ) : (
                <Badge color="orange" size="md">
                  {productData.length - productDataStatusInfo.pendingCount}/
                  {productData.length} Completed
                </Badge>
              ))}
          </AppFlexbox>
          {actionButton}
        </AppFlexbox>

        {description && (
          <AppText
            style={{
              padding: 8,
              textAlign: 'center',
              fontSize: 14,
              color: '#666',
              fontStyle: 'italic'
            }}
          >
            {description}
          </AppText>
        )}
        <AppText />
        {productData.length === 0 ? (
          <>
            <Divider style={{ marginTop: 8 }} />
            <AppText
              style={{
                textAlign: 'center',
                color: '#666',
                fontWeight: 500,
                fontSize: 14,
                padding: 12
              }}
            >
              {emptyMessage ||
                (isCancelled
                  ? 'All products in this order have been cancelled.'
                  : 'The order does not contain any registrations')}
            </AppText>
          </>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ padding: '8px 16px' }}>
                  <AppFlexbox>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      Form submission
                    </AppText>
                  </AppFlexbox>
                </Table.Th>
                <Table.Th style={{ padding: '8px 16px' }}>
                  <AppFlexbox style={{ justifyContent: 'end' }}>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      Status
                    </AppText>
                  </AppFlexbox>
                </Table.Th>
                <Table.Th style={{ padding: '8px 16px' }}>
                  <AppFlexbox style={{ justifyContent: 'end' }}>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      Total
                    </AppText>
                  </AppFlexbox>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {productData.map((p) => {
                const statusInfo = getRegFormSubmissionStatusInfo(
                  p.regFormSubmission
                );

                return (
                  <Table.Tr key={p.key}>
                    <Table.Td
                      style={{ padding: '8px 16px', alignContent: 'start' }}
                    >
                      <RegCartProductPreview
                        hideStatus
                        product={p}
                        urlBase={urlBase}
                      />
                    </Table.Td>
                    <Table.Td
                      style={{ padding: '8px 16px', alignContent: 'start' }}
                    >
                      <AppFlexbox style={{ justifyContent: 'end' }}>
                        <Badge
                          color={statusInfo.color}
                          variant={statusInfo.variant || 'filled'}
                        >
                          {statusInfo.label}
                        </Badge>
                      </AppFlexbox>
                    </Table.Td>
                    <Table.Td
                      style={{ padding: '8px 16px', alignContent: 'start' }}
                    >
                      <AppFlexbox style={{ justifyContent: 'end' }}>
                        <AppStack style={{ gap: 0, alignItems: 'end' }}>
                          <NumberFormatter
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                            style={{ fontSize: 14 }}
                            thousandSeparator
                            value={p.lineItem.subtotalInCents / 100}
                          />

                          {p.lineItem.coupons && p.lineItem.coupons.length > 0 && (
                            <OrderProductsCostInfo
                              data={p.lineItem.coupons.map((c) => ({
                                name: c.couponName,
                                amount: c.unitAmountInCents
                                  ? c.unitAmountInCents * -1
                                  : p.lineItem.subtotalInCents *
                                    (c.percentageAmount / 100) *
                                    -1
                              }))}
                              suffix="Discount"
                            />
                          )}

                          {p.lineItem.tax && p.lineItem.tax.length > 0 && (
                            <OrderProductsCostInfo
                              data={p.lineItem.tax.map((t) => ({
                                name: t.name,
                                amount:
                                  p.lineItem.taxInCents * p.lineItem.quantity,
                                rate: t.taxRate
                              }))}
                            />
                          )}
                        </AppStack>
                      </AppFlexbox>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </AppStack>
    </AppCard>
  ) : (
    <AppCard radius="md" shadow="xs" style={{ padding: 0 }} withBorder>
      <AppStack style={{ gap: 0 }}>
        <AppFlexbox style={{ padding: 16, paddingBottom: 8 }}>
          <Skeleton height={18} width="20%" />
        </AppFlexbox>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: '100%', padding: '8px 16px' }}>
                <AppFlexbox style={{ flex: 1 }}>
                  <Skeleton height={18} width="50%" />
                </AppFlexbox>
              </Table.Th>
              <Table.Th style={{ width: '100%', padding: '8px 16px' }}>
                <AppFlexbox style={{ flex: 1 }}>
                  <Skeleton height={18} width="100%" />
                </AppFlexbox>
              </Table.Th>
              <Table.Th style={{ width: '100%', padding: '8px 16px' }}>
                <AppFlexbox style={{ flex: 1, justifyContent: 'end' }}>
                  <Skeleton height={18} width="100%" />
                </AppFlexbox>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {Array.from(Array(2)).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Table.Tr key={i}>
                <Table.Td
                  style={{
                    width: '100%',
                    padding: '8px 16px',
                    alignContent: 'start'
                  }}
                >
                  <VendorOrderProductPreview loading />
                </Table.Td>
                <Table.Td
                  style={{ padding: '8px 16px', alignContent: 'start' }}
                >
                  <Skeleton height={18} width={60} />
                </Table.Td>
                <Table.Td
                  style={{ padding: '8px 16px', alignContent: 'start' }}
                >
                  <AppFlexbox style={{ justifyContent: 'end' }}>
                    <Skeleton height={18} width={100} />
                  </AppFlexbox>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </AppStack>
    </AppCard>
  );
};

OrderRegistrationsInfoCard.propTypes = {
  actionButton: PropTypes.any,
  description: PropTypes.string,
  emptyMessage: PropTypes.string,
  hideStatus: PropTypes.bool,
  isCancelled: PropTypes.bool,
  loading: PropTypes.bool,
  onViewTransactions: PropTypes.func,
  productData: PropTypes.array,
  title: PropTypes.string,
  urlBase: PropTypes.string
};

export default OrderRegistrationsInfoCard;
